import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import {
  buildPoseFileJson,
  splitSampleTimes,
  SPLIT_FPS,
  blazePoseLandmarkName,
  FrameLandmarks,
} from "../pose/clientPose";
import { extractionReducer, initialExtractionState } from "../pose/extractionState";
import { MODEL_VERSION, MODEL_URL, WASM_ROOT } from "../pose/mediapipeConfig";
import { drawSkeleton, SkeletonPoint } from "../pose/skeleton";

/**
 * web-pose-extraction step 4: the on-device analysis. Runs MediaPipe PoseLandmarker over the
 * dropped video at the split cadence, DRAWING each frame with its skeleton as it is analysed, then
 * lets the user play/scrub the frames and, behind "Adjust joints", see per-joint values, drag a
 * joint to tweak it, and reset a frame to the detected pose. Emits the pose file JSON via
 * onPoseFile once every frame is done (null while it runs, on a restart, and when it fails — the
 * upload page keeps Upload grey until it is non-null), and the frames analysed so far via onFrames.
 */

// The joints worth showing values for (the ones the backend consumes).
const VALUE_JOINTS = [0, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
const VALUE_JOINT_NAMES = VALUE_JOINTS.map((i) => blazePoseLandmarkName(i)!).filter(Boolean);

/** How often the page is told about new frames while the analysis runs (it re-guesses the technique
 * on each): every this many frames, and once more when the last one is done. */
const FRAMES_REPORT_EVERY = 5;

/** The frames analysed so far, for the page: the running technique guess and the progress line. */
export type ExtractionFrames = {
  frames: FrameLandmarks[];
  /** The video's own pixel size (landmarks are normalised to it). */
  width: number;
  height: number;
  /** How many frames there will be. */
  total: number;
  done: boolean;
};

type Props = {
  previewUrl: string;
  durationSec: number;
  onPoseFile: (json: string | null) => void;
  /** Told as frames are analysed (every few frames, and when the last is done). */
  onFrames?: (update: ExtractionFrames) => void;
  /** The cadence the server will split this upload at (frames pair with the split BY INDEX, so
   * the samples must sit exactly where the split frames will). The fixed 10 unless given. */
  fps?: number;
};

/** A frame's landmarks as canvas-pixel points keyed by landmark name, for drawSkeleton. */
function canvasPoints(frame: FrameLandmarks | undefined, width: number, height: number): Map<string, SkeletonPoint> {
  const points = new Map<string, SkeletonPoint>();
  for (const l of frame?.landmarks ?? []) {
    const name = blazePoseLandmarkName(l.index);
    if (name) points.set(name, { x: l.x * width, y: l.y * height });
  }
  return points;
}

export default function ExtractionStep({ previewUrl, durationSec, onPoseFile, onFrames, fps = SPLIT_FPS }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [state, dispatch] = useReducer(extractionReducer, undefined, initialExtractionState);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  // The clip's own shape, once known: the canvas is shown at it, at most 70% of the window tall, so
  // a portrait phone clip does not push the technique list a screen and a half down the page.
  const [aspect, setAspect] = useState<number | null>(null);
  const dims = useRef({ w: 0, h: 0 });
  const dragging = useRef<number | null>(null);
  // A newer run (a retry, a new clip) makes an older one stop touching the screen and the page.
  const runId = useRef(0);

  /** The video's current frame with one frame's skeleton over it. */
  const paint = useCallback((frame: FrameLandmarks | undefined, tweaked: boolean) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const g = canvas.getContext("2d");
    if (!g) return;
    g.drawImage(video, 0, 0);
    drawSkeleton(g, canvasPoints(frame, canvas.width, canvas.height), canvas.width, {
      joint: tweaked ? "#FFA500" : "#00CCFF",
      joints: VALUE_JOINT_NAMES,
      displayWidth: canvas.getBoundingClientRect().width,
    });
    setAspect((a) => a ?? video.videoWidth / video.videoHeight);
  }, []);

  const sampleTimes = splitSampleTimes(durationSec, fps);

  // ---- extraction run ---------------------------------------------------------------------------
  const runExtraction = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    const run = ++runId.current;
    const current = () => run === runId.current;
    onPoseFile(null);
    setError(null);
    setPlaying(false);
    dispatch({ type: "START", totalFrames: sampleTimes.length });
    try {
      const { FilesetResolver, PoseLandmarker } = await import("@mediapipe/tasks-vision");
      const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
      const landmarker = await PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL },
        runningMode: "VIDEO",
        numPoses: 1,
      });
      dims.current = { w: video.videoWidth, h: video.videoHeight };
      const done: FrameLandmarks[] = [];
      const report = (finished: boolean) =>
        onFrames?.({ frames: done.slice(), width: dims.current.w, height: dims.current.h, total: sampleTimes.length, done: finished });
      for (const t of sampleTimes) {
        await seekTo(video, t);
        if (!current()) { landmarker.close(); return; }
        const res = landmarker.detectForVideo(video, Math.round(t * 1000));
        const lm = res.landmarks?.[0] ?? [];
        const frame: FrameLandmarks = {
          landmarks: lm
            .map((p: any, index: number) => ({ index, x: p.x, y: p.y, z: p.z, visibility: p.visibility }))
            .filter((p: any) => blazePoseLandmarkName(p.index) !== undefined),
        };
        // The video is sitting on this very frame: show it with what was just found on it.
        paint(frame, false);
        done.push(frame);
        dispatch({ type: "FRAME_DONE", frame });
        if (done.length % FRAMES_REPORT_EVERY === 0) report(false);
      }
      landmarker.close();
      if (!current()) return;
      report(true);
      dispatch({ type: "COMPLETE" });
    } catch (e: any) {
      if (!current()) return;
      // No pose, no upload: the page keeps Upload grey, and this step offers a retry.
      setError(e?.message ?? "The on-device analysis could not run.");
      onPoseFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl, durationSec, fps]);

  useEffect(() => {
    // fresh video = fresh extraction
    dispatch({ type: "START", totalFrames: 0 });
    const video = videoRef.current;
    if (!video) return;
    const start = () => { runExtraction(); };
    if (video.readyState >= 2) start();
    else video.addEventListener("loadeddata", start, { once: true });
    return () => {
      video.removeEventListener("loadeddata", start);
      // A run still in flight for the old clip stops here. runId is a counter, not a DOM node: its
      // value at cleanup time is exactly the one wanted.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      runId.current++;
    };
  }, [runExtraction]);

  // Emit the pose file whenever review-state frames change (incl. after tweaks).
  useEffect(() => {
    if (state.phase !== "review" || state.frames.length === 0) return;
    onPoseFile(
      buildPoseFileJson(state.frames, {
        captureWidth: dims.current.w,
        captureHeight: dims.current.h,
        modelVersion: MODEL_VERSION,
        fps,
      })
    );
  }, [state.phase, state.frames, onPoseFile, fps]);

  // ---- review: seek + draw ----------------------------------------------------------------------
  useEffect(() => {
    if (state.phase !== "review") return;
    const video = videoRef.current;
    if (!video) return;
    seekTo(video, sampleTimes[state.currentFrame] ?? 0).then(draw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentFrame, state.phase, state.frames]);

  // play mode: advance frames on a timer at ~the split cadence
  useEffect(() => {
    if (!playing || state.phase !== "review") return;
    const id = setInterval(() => {
      dispatch({ type: "GOTO_FRAME", index: (state.currentFrame + 1) % state.frames.length });
    }, Math.round(1000 / fps));
    return () => clearInterval(id);
  }, [playing, state.phase, state.currentFrame, state.frames.length, fps]);

  const draw = useCallback(() => {
    if (state.phase !== "review") return;
    paint(state.frames[state.currentFrame], state.tweakedFrames.has(state.currentFrame));
  }, [state, paint]);

  useEffect(draw, [draw]);

  // ---- tweak: drag the nearest joint -------------------------------------------------------------
  const canvasPoint = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (state.phase !== "review" || !adjusting) return;
    const p = canvasPoint(e);
    const frame = state.frames[state.currentFrame];
    let best = -1, bestD = 0.04; // only grab within ~4% of the frame
    for (const l of frame?.landmarks ?? []) {
      if (!VALUE_JOINTS.includes(l.index)) continue;
      const d = Math.hypot(l.x - p.x, l.y - p.y);
      if (d < bestD) { bestD = d; best = l.index; }
    }
    if (best >= 0) {
      dragging.current = best;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (dragging.current == null) return;
    const p = canvasPoint(e);
    dispatch({ type: "TWEAK", frameIndex: state.currentFrame, landmarkIndex: dragging.current, x: p.x, y: p.y });
  };

  const onPointerUp = () => { dragging.current = null; };

  // ---- render ------------------------------------------------------------------------------------
  const frame = state.frames[state.currentFrame];
  const percent = Math.round(state.progress * 100);
  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">On-device pose extraction</h2>
        {state.phase === "review" && state.tweakedFrames.has(state.currentFrame) && (
          <button
            type="button"
            onClick={() => dispatch({ type: "RESET_FRAME", frameIndex: state.currentFrame })}
            className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
          >
            <RotateCcw className="h-3 w-3" /> reset frame to detected
          </button>
        )}
      </div>

      {/* hidden video drives seeking; the canvas is the display — the clip, with the skeleton found
          on each frame drawn over it as the analysis reaches that frame */}
      <video ref={videoRef} src={previewUrl} className="hidden" muted playsInline preload="auto" />
      <canvas
        ref={canvasRef}
        className={aspect ? "block mx-auto rounded-lg bg-black touch-none" : "w-full rounded-lg bg-black touch-none"}
        style={aspect ? { width: `min(100%, calc(70vh * ${aspect}))`, aspectRatio: String(aspect) } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />

      {state.phase === "extracting" && !error && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            {state.totalFrames > 0
              ? <>Analysing frame {Math.min(state.frames.length + 1, state.totalFrames)} of {state.totalFrames} · {percent}%</>
              : <>Loading the pose model…</>}
          </div>
          <div className="h-2 rounded bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>
      )}

      {state.phase === "review" && (
        <>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="px-3 py-1 text-xs rounded-full bg-slate-900 text-white"
            >
              {playing ? "pause" : "play"}
            </button>
            <input
              type="range"
              min={0}
              max={Math.max(0, state.frames.length - 1)}
              value={state.currentFrame}
              onChange={(e) => { setPlaying(false); dispatch({ type: "GOTO_FRAME", index: Number(e.target.value) }); }}
              className="flex-1 accent-emerald-500"
            />
            <span className="text-xs text-slate-500 tabular-nums">
              {state.currentFrame + 1}/{state.frames.length}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-emerald-700">All {state.frames.length} frames analysed on this device.</span>
            <button
              type="button"
              onClick={() => setAdjusting((a) => !a)}
              className="text-slate-500 underline hover:text-slate-700"
            >
              {adjusting ? "Done adjusting" : "Adjust joints"}
            </button>
          </div>
          {adjusting && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-slate-600 font-mono">
                {(frame?.landmarks ?? [])
                  .filter((l) => VALUE_JOINTS.includes(l.index))
                  .map((l) => (
                    <div key={l.index} className="flex justify-between gap-2">
                      <span className="truncate">{blazePoseLandmarkName(l.index)}</span>
                      <span className="tabular-nums text-slate-400">
                        {(l.x * 100).toFixed(1)},{(l.y * 100).toFixed(1)} · {((l.visibility ?? 1) * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
              </div>
              <p className="text-[11px] text-slate-400">
                drag a joint to correct it · tweaked frames show orange dots
              </p>
            </>
          )}
        </>
      )}

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-xs text-red-700">
            The on-device analysis could not finish ({error}). The video cannot be uploaded without it.
          </p>
          <button
            type="button"
            onClick={() => runExtraction()}
            className="shrink-0 rounded-full bg-slate-900 px-3 py-1 text-xs text-white"
          >
            Retry analysis
          </button>
        </div>
      )}
    </div>
  );
}

function seekTo(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - t) < 1 / 240 && video.readyState >= 2) return resolve();
    const done = () => { video.removeEventListener("seeked", done); resolve(); };
    video.addEventListener("seeked", done);
    video.currentTime = t;
  });
}
